import { ExclamationCircleIcon, InformationCircleIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { cn } from '@/lib/cn';

type Intent = 'success' | 'error' | 'warning' | 'info';

interface MessageBarProps {
  intent: Intent;
  title?: React.ReactNode;
  children: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

const INTENT_BG: Record<Intent, string> = {
  success: 'bg-green-50 border-green-200 text-green-900',
  error: 'bg-red-50 border-red-200 text-red-900',
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
  info: 'bg-blue-50 border-blue-200 text-blue-900',
};

const INTENT_ICON_COLOR: Record<Intent, string> = {
  success: 'text-green-600',
  error: 'text-red-600',
  warning: 'text-amber-600',
  info: 'text-blue-600',
};

const INTENT_ICON: Record<Intent, React.ReactNode> = {
  success: <CheckCircleIcon className="h-5 w-5" />,
  error: <ExclamationCircleIcon className="h-5 w-5" />,
  warning: <ExclamationTriangleIcon className="h-5 w-5" />,
  info: <InformationCircleIcon className="h-5 w-5" />,
};

// Inline contextual banner. Used at the top of a page or section to surface
// a state that's relevant for the whole view (impersonation, error fetching
// data, important notices).
export function MessageBar(props: MessageBarProps) {
  const { intent, title, children, action, className } = props;
  return (
    <div
      role="status"
      className={cn(
        'flex items-start gap-3 rounded-md border px-4 py-3 text-sm',
        INTENT_BG[intent],
        className,
      )}
    >
      <span className={cn('mt-0.5 shrink-0', INTENT_ICON_COLOR[intent])}>
        {INTENT_ICON[intent]}
      </span>
      <div className="flex-1 min-w-0">
        {title && <p className="font-semibold">{title}</p>}
        <div className={cn(title ? 'mt-0.5' : '')}>{children}</div>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
